import { internalReference } from './header';
import {
  bynderImageProjection,
  computedInternalUrl,
  directInternalReferenceUrl,
  internalReferenceUrl,
  spreadBynderImageProjection,
} from './projection';
import { namespaceType } from './namespace';

const productType = namespaceType('product');
const documentBlockType = namespaceType('documentBlock');
const applicationsBlockType = namespaceType('applicationsBlock');
const linkType = namespaceType('link');
const articleType = namespaceType('article');
const textSectionType = namespaceType('textSection');
const imageTextBlockType = namespaceType('imageTextBlock');
const featureSummaryType = namespaceType('featureSummary');
const productTypeBlock = namespaceType('productType');
const heroBannerSlideshowType = namespaceType('heroBannerSlideshow');
const heroBannerType = namespaceType('heroBanner');
const contentCarouselType = namespaceType('contentCarousel');
const videoImageBlockType = namespaceType('videoImageBlock');
const logoWallType = namespaceType('logoWall');
const anchorType = namespaceType('anchor');
const headlineType = namespaceType('headline');
const quoteBlockType = namespaceType('quote');
const milestoneTimelineType = namespaceType('milestoneTimeline');
const masterTextBlockType = namespaceType('masterTextBlock');
const productSlideshowType = namespaceType('productSlideshow');
const productCarouselType = namespaceType('productCarousel');
const multiColumnBannerType = namespaceType('multiColumnBanner');
const galleryType = namespaceType('gallery');
const productVariantSectionType = namespaceType('productVariantSection');
const productSummaryType = namespaceType('productSummary');
const middleBannerType = namespaceType('middleBanner');
const textIconListType = namespaceType('textIconList');
const imageGridType = namespaceType('imageGrid');
const contactSupportBlockType = namespaceType('contactSupportBlock');

export const PRODUCT_REFERENCE_INFO = `
productRef->{
  _id,
  _type,
  name,
  "productURL": select(
    defined(parentPage->parentPage->slug.current) => "/products/" + parentPage->parentPage->slug.current + "/" + parentPage->slug.current + "/" + slug.current,
    defined(parentPage->slug.current) => "/products/" + parentPage->slug.current + "/" + slug.current,
    _type == "${productType}" && defined(subcategory->parentCategory->slug.current) => "/products/" + subcategory->parentCategory->slug.current + "/" + subcategory->slug.current + "/" + slug.current,
    "/products/" + slug.current
  )
}
`;

export const getInternalUrlQuery = (contentName: string) => `
  ${contentName}[]{
    ...,
    markDefs[]{
      ...,
      "href": select(
        linkType == "page" => ${internalReference('page')}.url,
        href
      ),
      "isOpenInNewTab": select(
        linkType == "page" => blank,
        true
      ),
    }
  }
`;

/* ========================================================================= */

export const documentBlockProjection = `
_type == "${documentBlockType}" => {
  ...,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
  "defaultExpanded": [],
  documentSections[]{
    ...,
    documentItems[]{
      ...,
      "bynderDocument": file->bynderDocument
    }
  }
}
`;

export const applicationsBlockProjection = `
  _type == "${applicationsBlockType}" => {
    ...,
    buttons[]{
      ...,
      link{
        ...,
        "url": ${internalReferenceUrl},
        _type == "${linkType}" => {
          "page": page->slug.current,
          "post": post->slug.current
        },
      },
    },
    "items": select(
      selectionMode == "manual" => items[]->{
        ...,
        "thumbnail": customSeo.metaImage{ ..., "url": selectedUrl, "alt": textMetaproperties[name == 'Alt_text'][0].value },
        "date": coalesce(date, _updatedAt),
      },
      selectionMode == "auto" => *[_type == "${articleType}" && type == ^.contentType && references(^.tag._ref) && defined(slug.current)] | order(_updatedAt desc) [0...3]{
        ...,
        "thumbnail": customSeo.metaImage{ ..., "url": selectedUrl, "alt": textMetaproperties[name == 'Alt_text'][0].value },
        "date": coalesce(date, _updatedAt),
      }
    )
  }
`;

export const textSectionProjection = `
  _type == "${textSectionType}" => {
    ...,
    ${getInternalUrlQuery('content')},
    columns[]{
      ...,
      ${getInternalUrlQuery('content')},
    }
  }
`;

export const imageTextBlockProjection = `
_type == "${imageTextBlockType}" => {
  ...,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
  buttons[]{
    ...,
    link{ href, isOpenInNewTab }
  },
  ${getInternalUrlQuery('body')},
}
`;

export const featureSummaryProjection = `
_type == "${featureSummaryType}" => {
  ...,
  contentBlocks[]{
    ...,
    _key,
    ${bynderImageProjection('icon', 'bynderIcon')},
    bynderIcon,
    textLink{
      text,
      isBold,
      "link": {
        "type": link.type,
         "href": ${directInternalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    }
  }
}
`;

export const productTypeProjection = `
  _type == "${productTypeBlock}" => {
  ...,
  buttons[]{
    ...,
    link{
      ...,
      "url": ${internalReferenceUrl},
    }
  },
  "productCards": productCards[]{
    ...,
    _id,
    "productDetail": productDetail->{
      ...,
      _id,
      "name": coalesce(name, title),
      "productSummary": coalesce(productSummary, description),
      "thumbnailImage": customSeo.metaImage{
        ...,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      "url": ${computedInternalUrl},
    }
  }
}
`;

export const contactSupportBlockProjection = `
  _type == "${contactSupportBlockType}" => {
    ...,
    imageLink{
      ${bynderImageProjection('image', 'bynderImage', "coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt)")},
      bynderImage,
      "alt": coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt),
      link{
        ...,
        "url": ${internalReferenceUrl},
      }
    },
    contactInfo[]{
      ...,
      icon{
        icon,
        ${bynderImageProjection('iconImage', 'bynderIconImage')},
        bynderIconImage
      },
      textLink{
        text,
        isBold,
        "link": {
          "href": ${internalReferenceUrl},
          "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
        }
      }
    },
    ctaButton{
      ...,
      "link": {
        ...,
        "href": ${internalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    }
  }
`;

export const heroBannerSlideshowProjection = `
  _type in ["${heroBannerSlideshowType}", "${heroBannerType}"] => {
    ...,
    _type,
    isActive,
    "slides": slides[]{
      ...,
      _type,
      _key,
      isVideo,
      ${bynderImageProjection('image', 'bynderImage')},
      bynderImage,
      ${bynderImageProjection('poster', 'bynderPoster')},
      bynderPoster,
      bynderVideo,
      "videoFile": select(
        defined(bynderVideo) => {
          "_type": "bynder.asset",
          "id": bynderVideo.id,
          "name": bynderVideo.name,
          "description": bynderVideo.description,
          "selectedUrl": bynderVideo.selectedUrl,
          "videoUrl": bynderVideo.videoUrl,
          "url": coalesce(bynderVideo.videoUrl, bynderVideo.selectedUrl, bynderVideo.url)
        },
        null
      ),
      "videoUrl": video,
      "buttons": buttons[]{
        label,
        style,
        "url": ${directInternalReferenceUrl},
        "anchor": coalesce(link.anchor, linkWithAskGreg.anchor),
        "linkType": coalesce(link.type, linkWithAskGreg.type),
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, linkWithAskGreg.isOpenInNewTab)
      },
    }
  }
`;

export const contentCarouselProjection = `
  _type == "${contentCarouselType}" => {
    ...,
    "description": description,
    "backgroundColor": backgroundColor,
    "switchImageAndText": switchImageAndText,
    ${getInternalUrlQuery('description')},
    slides[]{
      ...,
      "vimeoURL": video,
      ${bynderImageProjection('image', 'bynderImage')},
      bynderImage,
      ${getInternalUrlQuery('description')},
    }
  }
`;

export const videoImageBlockProjection = `
_type == "${videoImageBlockType}" => {
  ...,
  _type,
  "videoUrl": video,
  textOverlay {
    heading,
    description
  },
  ${bynderImageProjection('thumbnail', 'bynderThumbnail')},
  bynderThumbnail,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
   buttons[]{
    label,
    "style": style,
    "url": ${directInternalReferenceUrl},
    "anchor": link.anchor,
    "linkType": link.type,
    "isOpenInNewTab": link.isOpenInNewTab
  }
}
`;

export const logoWallProjection = `
  _type == "${logoWallType}" => {
    ...,
    "backgroundColor": backgroundColor,
    heading,
    description[]{
      ...,
      markDefs[]{
        ...,
        _type == "${linkType}" => {
          ...,
          "url": href,
          "isOpenInNewTab": coalesce(blank, false)
        }
      }
    },
    textAlignment,
      "logos": logos[] {
        _key,
        "bynderLogoImage": select(
          defined(selectedUrl) || defined(url) || defined(datUrl) || defined(previewUrl) || defined(previewImg) => {
            "_type": "bynder.asset",
            "id": id,
            "name": name,
            "description": description,
            "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt),
            "selectedUrl": selectedUrl,
            "url": coalesce(url, selectedUrl, datUrl, previewUrl, previewImg),
            "width": width,
            "height": height,
            "aspectRatio": aspectRatio
          },
          null
        ),
        "url": coalesce(
          selectedUrl,
          url,
          datUrl,
          previewUrl,
          previewImg,
          asset->url
      ),
      "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt, asset->altText),
    },
    ${getInternalUrlQuery('description')}
  }
`;

export const anchorProjection = `
_type == "${anchorType}" => {
  "id": id.current
}
`;

export const headlineProjection = `
_type == "${headlineType}" => {
  ...,
  title,
  shortSummary,
  "backgroundColor": backgroundColor,
  "headingAlignment": coalesce(headingAlignment, "left"),
  "isActive": coalesce(isActive, false)
}
`;

export const quoteBlockProjection = `
_type == "${quoteBlockType}" => {
  ...,
  ${bynderImageProjection('icon', 'bynderIcon')},
  bynderIcon,
  heading,
  subheading,
  "backgroundColor": backgroundColor
}
`;

export const milestoneTimelineProjection = `
  _type == "${milestoneTimelineType}" => {
    ...,
    heading,
    "backgroundColor": backgroundColor,
    "timelineItems": timelineItems[] {
      ...,
      yearDecade,
      "description": description,
    },
    ${getInternalUrlQuery('description')},
  }
`;

export const masterTextBlockProjection = `
  _type == "${masterTextBlockType}" => {
    ...,
    heading,
    hyperlink {
      text,
      isBold,
      "link": {
        "href": ${directInternalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    },
    "description": description,
    "backgroundColor": backgroundColor,
    "layoutOptions": layoutOptions,
    "textBlocks": textBlocks[] {
      ...,
      ${getInternalUrlQuery('description')},
    },
    ${getInternalUrlQuery('description')},
  }
`;

export const productSlideshowProjection = `
  _type == "${productSlideshowType}" => {
    ...,
    "backgroundColor": backgroundColor,
    "productSlides": productSlides[] {
      ...,
      heading,
      subheading,
      description,
      ${PRODUCT_REFERENCE_INFO},
      productDescription,
      "productThumbnail": productRef->customSeo.metaImage{
        ...,
        "url": selectedUrl,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      hyperlinkLabel
    }
  }
`;

export const productCarouselProjection = `
  _type == "${productCarouselType}" => {
    ...,
    "backgroundColor": backgroundColor,
    "productCarouselItems": productCarouselItems[] {
      ...,
      ${PRODUCT_REFERENCE_INFO},
      productDescription,
      "productThumbnail": productRef->customSeo.metaImage{
        ...,
        "url": selectedUrl,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      hyperlinkLabel
    }
  }
`;

export const multiColumnBannerProjection = `
  _type == "${multiColumnBannerType}" => {
    ...,
    heading,
    "layoutOptions": layoutOptions,
    "textBlocks": textBlocks[] {
      ...,
      ${getInternalUrlQuery('description')},
    },
    "headingTextAlignment": headingTextAlignment,
    "backgroundColor": backgroundColor
  }
`;

export const galleryProjection = `
  _type == "${galleryType}" => {
    ...,
    "images": images[]{
      "_key": _key,
      ${spreadBynderImageProjection('bynderSrc')},
    },
  }
`;

export const productVariantProjection = `
  _type == "${productVariantSectionType}" => {
    ...,
    buttons[]{
      ...,
      link{
        ...,
        "url": ${internalReferenceUrl},
      }
    },
    productVariantItems[]{
      name,
      description,
      variantValue,
      campaignLink{
        ...,
        "url": ${internalReferenceUrl},
      },
      ${bynderImageProjection('defaultImage', 'defaultImage->bynderImage')},
      "bynderImage": defaultImage->bynderImage,
    },
  }
`;

export const productSummaryProjection = `
  _type == "${productSummaryType}" => {
    ...,
    ${bynderImageProjection('image', 'bynderImage')},
    bynderImage,
    ${getInternalUrlQuery('description')},
    ctaUnderHeadline[]{
      ...,
      "url": select(
        linkType == "internal" => internalReference->{
          "computedUrl": ${computedInternalUrl}
        }.computedUrl,
        url
      ),
    },
    ctaUnderDescription[]{
      ...,
      "url": select(
        linkType == "internal" => internalReference->{
          "computedUrl": ${computedInternalUrl}
        }.computedUrl,
        url
      ),
    }
  }
`;

export const middleBannerProjection = `
  _type == "${middleBannerType}" => {
    ...,
    ${getInternalUrlQuery('bodyContent')},
    buttonOptional{
      ...,
      "linkOptional": link{
        ...,
        "url": ${internalReferenceUrl},
        "isOpenInNewTab": coalesce(isOpenInNewTab, newTab)
      }
    }
  }
`;

export const textIconListProjection = `
  _type == "${textIconListType}" => {
    ...,
    ${getInternalUrlQuery('description')},
    textIconItemList{
      ...,
      subheading,
      textIconItems[]{
        ...,
        ${bynderImageProjection('icon', 'bynderIcon')},
        bynderIcon,
        title{
          ...,
          isBold,
          text,
          link{
            ...,
            "type": link.type,
            "href": ${internalReferenceUrl},
            "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
          },
        },
        ${getInternalUrlQuery('description')},
      }
    }
  }
`;

export const imageGridProjection = `
  _type == "${imageGridType}" => {
    ...,
    images[] {
      ...,
      ${bynderImageProjection('image', 'bynderImage', "coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt)")},
      bynderImage,
      "alt": coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt),
    },
  }
`;

export const PAGE_BUILDER_PROJECTION = `
  "pageBuilder" : pageBuilder[]{
    ...,
    ${applicationsBlockProjection},
    ${textSectionProjection},
    ${imageTextBlockProjection},
    ${featureSummaryProjection},
    ${productTypeProjection},
    ${heroBannerSlideshowProjection},
    ${contentCarouselProjection},
    ${videoImageBlockProjection},
    ${documentBlockProjection},
    ${logoWallProjection},
    ${headlineProjection},
    ${anchorProjection},
    ${quoteBlockProjection},
    ${milestoneTimelineProjection},
    ${masterTextBlockProjection},
    ${multiColumnBannerProjection},
    ${productSlideshowProjection},
    ${productCarouselProjection},
    ${contactSupportBlockProjection},
    ${galleryProjection},
    ${productVariantProjection},
    ${productSummaryProjection},
    ${middleBannerProjection},
    ${textIconListProjection},
    ${imageGridProjection},
  }
`;
